import { type PropsWithChildren, createContext, useContext } from 'react';

import { ColorPalette, generatePalette } from '@aicon-dev/caas-ui';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { createGlobalStyle } from 'styled-components';

import { getAppSettings } from '../../lib/apis';

import type { AppSettingsDto } from '../../lib/types';

const GlobalStyle = createGlobalStyle<{ palette: ColorPalette }>`
  :root {
    --color-primary-50: ${({ palette }) => palette[50]};
    --color-primary-100: ${({ palette }) => palette[100]};
    --color-primary-200: ${({ palette }) => palette[200]};
    --color-primary-300: ${({ palette }) => palette[300]};
    --color-primary-400: ${({ palette }) => palette[400]};
    --color-primary-500: ${({ palette }) => palette[500]};
    --color-primary-600: ${({ palette }) => palette[600]};
    --color-primary-700: ${({ palette }) => palette[700]};
    --color-primary-800: ${({ palette }) => palette[800]};
    --color-primary-900: ${({ palette }) => palette[900]};

    --chat-message-background-me: var(--color-primary-500) !important;
    --chat-color-primary: var(--color-primary-500) !important;;
    --chat-color-secondary: var(--color-primary-600) !important;
    --chat-channel-background: color-mix(in srgb, var(--color-primary-50) 20%, #DBE7EF) !important;
}
  ::selection {
    color: #fff;
    background: ${({ palette }) => palette[500]};
  }
`;

export const ThemeContext = createContext<AppSettingsDto>({
  appName: 'caasclient',
  slogan: '안전하고 투명한 건설현장',
  primaryColor: '#16C471',
});

export const ThemeProvider = (props: PropsWithChildren) => {
  const { children } = props;
  const appName = Cookies.get('APP_NAME');

  const { data } = useQuery({
    queryKey: ['getAppSettings', appName],
    enabled: !!appName,
    queryFn: async () => {
      if (!appName) return null;
      const { data } = await getAppSettings(appName);
      return data;
    },
  });

  return (
    <ThemeContext.Provider value={{ ...data }}>
      <GlobalStyle palette={generatePalette(data?.primaryColor || '#16C471')} />
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);
